import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Brain, Send, Loader2, Sparkles, TrendingUp, DollarSign, Users } from "lucide-react";
import type { SalesReportData } from "@re/shared";

const SUGGESTED_QUESTIONS = [
  "Which marketing channel is performing best and why?",
  "What's the cost efficiency of Google vs Meta campaigns?",
  "Which pipeline has the best ROAS?",
  "Suggest ways to improve our conversion rate",
  "What trends do you see in the data?",
];

export default function AnalysisPage() {
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const responseRef = useRef<HTMLDivElement>(null);

  const { data: reportData } = useQuery<SalesReportData>({
    queryKey: ["/api/report"],
  });

  const handleAnalyze = async (q?: string) => {
    const queryText = q || question;
    if (!queryText.trim()) return;

    setIsLoading(true);
    setResponse("");

    try {
      const res = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: queryText, data: reportData }),
      });

      if (!res.ok) throw new Error("Analysis request failed");

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.content) {
              setResponse((prev) => prev + event.content);
            }
            if (event.error) {
              setResponse("Error: " + event.error);
            }
          } catch {}
        }
      }
    } catch (error) {
      setResponse("Failed to get analysis. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [response]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Brain className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-analysis-title">
            AI Analysis
          </h1>
        </div>
        <p className="text-muted-foreground">
          Ask questions about your marketing data and get AI-powered insights
        </p>
      </div>

      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                Total Spend
              </div>
              <p className="text-2xl font-bold">£{reportData.overall.totalCost.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Conversions
              </div>
              <p className="text-2xl font-bold">{reportData.overall.totalConversions.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                ROAS
              </div>
              <p className="text-2xl font-bold">{reportData.overall.roas.toFixed(2)}x</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Ask a Question
          </CardTitle>
          <CardDescription>
            Get insights about your marketing performance, campaign comparisons, or optimization suggestions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_QUESTIONS.map((q, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="cursor-pointer"
                onClick={() => {
                  setQuestion(q);
                  handleAnalyze(q);
                }}
                data-testid={`badge-question-${i}`}
              >
                {q}
              </Badge>
            ))}
          </div>

          <div className="flex gap-2">
            <Textarea
              placeholder="Ask anything about your marketing data..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAnalyze();
                }
              }}
              className="min-h-[80px] resize-none"
              data-testid="input-question"
            />
            <Button
              onClick={() => handleAnalyze()}
              disabled={isLoading || !question.trim()}
              className="self-end"
              data-testid="button-analyze"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {(response || isLoading) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Analysis
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              ref={responseRef}
              className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap"
              data-testid="text-response"
            >
              {response || "Analyzing your data..."}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
